"use client"

import { useState, useEffect } from "react"
import Sidebar from "../components/Sidebar.js"
import Modal from "../components/CreateEventModal.js"
import { getAllEvents, toggleRegistrationStatus, deleteEvent } from "../comman/Event.js"
import { FaEdit, FaTrash } from "react-icons/fa"
import { IoMdAdd, IoMdSearch } from "react-icons/io"

const EventTable = () => {
  const [search, setSearch] = useState("")
  const [events, setEvents] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [eventToEdit, setEventToEdit] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchEvents = async () => {
    try {
      setLoading(true)
      const eventData = await getAllEvents()
      setEvents(eventData)
      setLoading(false)
    } catch (error) {
      console.error("Failed to fetch events", error)
      setError("Failed to load events. Please try again.")
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, []) //Fixed useEffect dependency issue

  const handleSearch = (e) => {
    setSearch(e.target.value)
  }

  const filteredEvents = events.filter((event) => event.eventName?.toLowerCase().includes(search.toLowerCase()))

  const toggleRegistration = async (eventId) => {
    try {
      const updatedEvent = await toggleRegistrationStatus(eventId)
      setEvents(events.map((event) => (event._id === eventId ? { ...event, status: updatedEvent.status } : event)))
    } catch (error) {
      console.error("Error updating registration:", error)
    }
  }

  const handleCreateEvent = (eventData) => {
    setEvents([...events, eventData])
    setIsModalOpen(false)
  }

  const handleEditEvent = (eventId) => {
    const eventToEdit = events.find((event) => event._id === eventId)
    setEventToEdit(eventToEdit)
    setIsModalOpen(true)
  }

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      try {
        await deleteEvent(eventId)
        setEvents(events.filter((event) => event._id !== eventId))
      } catch (error) {
        console.error("Error deleting event:", error)
      }
    }
  }

  const formatEventDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatEventTime = (timeFrom, timeTo) => {
    const formatTime = (time) => {
      const date = new Date(`1970-01-01T${time}`)
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    }
    return `${formatTime(timeFrom)} - ${formatTime(timeTo)}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#12143F]"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 md:ml-64 w-full max-w-[100vw] overflow-hidden">
        <div className="max-w-full overflow-hidden">
          <h1 className="text-2xl md:text-3xl font-bold text-[#12143F] mb-6">Event Management</h1>
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 overflow-hidden">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-[#12143F] focus:border-[#12143F]"
                  placeholder="Search events"
                  value={search}
                  onChange={handleSearch}
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <IoMdSearch className="w-5 h-5 text-gray-500" />
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-[#12143F] text-white rounded-md hover:bg-opacity-90 transition duration-300"
              >
                <IoMdAdd size={18} className="mr-2" />
                Add Event
              </button>
            </div>
            <div className="overflow-x-auto -mx-4 md:-mx-6">
              <div className="inline-block min-w-full align-middle p-4 md:p-6">
                <div className="overflow-hidden border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-[#12143F]">
                      <tr>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Event Name
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Description
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Activities
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Location
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Price
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Seats
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Date & Time
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Registration
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredEvents.map((event) => (
                        <tr key={event._id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px] truncate">{event.eventName}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 max-w-[300px]">
                            <div className="line-clamp-2">{event.aboutEvent}</div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px]">
                            <div className="line-clamp-2">{event.activities}</div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 max-w-[150px] truncate">{event.location}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">₹{event.ticketPrice}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">{event.maxSeats}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                            <div>{formatEventDate(event.date)}</div>
                            <div className="text-gray-500">{formatEventTime(event.timeFrom, event.timeTo)}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={event.status}
                                onChange={() => toggleRegistration(event._id)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                            </label>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => handleEditEvent(event._id)}
                                className="text-[#12143F] hover:text-opacity-80 transition-colors duration-200"
                              >
                                <FaEdit size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteEvent(event._id)}
                                className="text-red-500 hover:text-red-700 transition-colors duration-200"
                              >
                                <FaTrash size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setEventToEdit(null)
        }}
        onSubmit={handleCreateEvent}
        eventToEdit={eventToEdit}
      />
    </div>
  )
}

export default EventTable

